/**
 * Unified Admin Layout
 * Dark theme, glassmorphic design, persistent sidebar
 * No layout flashing, stable scroll position
 */
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

import {
  Bell,
  BookOpen,
  Buildings,
  CaretRight,
  ChartBar,
  ChatText,
  CurrencyDollar,
  Database,
  Envelope,
  FileText,
  GearSix,
  Handshake,
  ListDashes,
  MagnifyingGlass,
  Pulse,
  Queue,
  Robot,
  Scales,
  Shield,
  SignOut,
  Sparkle,
  SquaresFour,
  Storefront,
  UserGear,
  UserPlus,
  UsersThree,
  X
} from '@phosphor-icons/react';
const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Initialize notifications from localStorage or empty array
  // This ensures "read" state persists across sessions
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('admin_notifications_read');
      const readIds = saved ? JSON.parse(saved) : [];
      // Start with empty notifications - real notifications will come from backend
      // For now, return empty array to remove fake test data
      return [];
    } catch {
      return [];
    }
  });
  
  // Persist read state to localStorage whenever notifications change
  useEffect(() => {
    try {
      const readIds = notifications.filter(n => n.read).map(n => n.id);
      localStorage.setItem('admin_notifications_read', JSON.stringify(readIds));
    } catch (e) {
      console.error('Failed to persist notification read state:', e);
    }
  }, [notifications]);

  // Navigation items
  const navItems = [
    {
      section: 'Overview',
      items: [
        { path: '/admin/dashboard', icon: SquaresFour, label: 'Dashboard' },
      ]
    },
    {
      section: 'Management',
      items: [
        { path: '/admin/organizations', icon: Buildings, label: 'Organizations' },
        { path: '/admin/users', icon: UsersThree, label: 'Users' },
        { path: '/admin/partners', icon: Handshake, label: 'Partners' },
        { path: '/admin/affiliates', icon: UserPlus, label: 'Affiliates' },
        { path: '/admin/billing', icon: CurrencyDollar, label: 'Billing & Finance' },
      ]
    },
    {
      section: 'Platform',
      items: [
        { path: '/admin/analytics', icon: ChartBar, label: 'Analytics' },
        { path: '/admin/llm-usage', icon: Robot, label: 'LLM Usage' },
        { path: '/admin/backups', icon: Database, label: 'Backups' },
        { path: '/admin/logs', icon: FileText, label: 'Logs & Audit' },
      ]
    },
    {
      section: 'Marketplace',
      items: [
        { path: '/admin/automation-review', icon: Queue, label: 'Automation Review' },
        { path: '/admin/app-review', icon: Storefront, label: 'App Review' },
      ]
    },
    {
      section: 'Content',
      items: [
        { path: '/admin/blogs', icon: BookOpen, label: 'Blogs' },
        { path: '/admin/ebooks', icon: Sparkle, label: 'Ebooks' },
        { path: '/admin/ebook-downloads', icon: Envelope, label: 'Ebook Leads' },
        { path: '/admin/messages', icon: ChatText, label: 'Messages' },
        { path: '/admin/legal', icon: Scales, label: 'Legal Docs' },
        { path: '/admin/docs', icon: FileText, label: 'Documentation' },
      ]
    },
    {
      section: 'System',
      items: [
        { path: '/admin/admin-users', icon: UserGear, label: 'Admin Users' },
        { path: '/admin/tracking-diagnostics', icon: Pulse, label: 'Tracking Diagnostics' },
        { path: '/admin/settings', icon: GearSix, label: 'Settings' },
      ]
    }
  ];

  // Get breadcrumb from current path
  const getBreadcrumb = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    if (segments.length === 1) return [{ label: 'Admin', path: '/admin' }];
    
    return segments.map((segment, index) => {
      const path = '/' + segments.slice(0, index + 1).join('/');
      const label = segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');
      return { label, path };
    });
  };

  const breadcrumbs = getBreadcrumb();
  const notificationsRef = useRef(null);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  const handleLogout = async () => {
    // Use AuthContext logout - handles API call, localStorage clearing, forceAuthRemount
    await logout();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-screen bg-[#0F0F0F]/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                <Shield size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg">Admin</span>
            </motion.div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X size={18} /> : <ListDashes size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-8rem)]">
          {navItems.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              {sidebarOpen && (
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
                  {section.section}
                </h3>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group ${
                        isActive
                          ? 'bg-gradient-to-r from-[#6366F1]/20 to-[#EC4899]/20 border border-[#6366F1]/30 text-white'
                          : 'hover:bg-white/5 text-gray-400 hover:text-white'
                      }`}
                    >
                      <Icon size={18} className={isActive ? 'text-[#6366F1]' : ''} />
                      {sidebarOpen && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                      {isActive && sidebarOpen && (
                        <CaretRight size={14} className="ml-auto text-[#6366F1]" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Admin Profile */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-[#0F0F0F]/95 backdrop-blur-xl">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center text-sm font-bold">
                A
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">Admin User</p>
                <p className="text-xs text-gray-500">Super Admin</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Logout"
              >
                <SignOut size={16} />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-2 hover:bg-white/10 rounded-lg transition-colors flex justify-center"
              title="Logout"
            >
              <SignOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10">
          <div className="px-6 py-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && <CaretRight size={14} />}
                  <Link
                    to={crumb.path}
                    className={`hover:text-white transition-colors ${
                      index === breadcrumbs.length - 1 ? 'text-white font-medium' : ''
                    }`}
                  >
                    {crumb.label}
                  </Link>
                </React.Fragment>
              ))}
            </div>

            {/* MagnifyingGlass & Actions */}
            <div className="flex items-center gap-4">
              {/* MagnifyingGlass */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search admin panel..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50 transition-colors"
                  />
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center gap-2">
                <div className="relative" ref={notificationsRef}>
                  <button 
                    onClick={() => setNotificationsOpen(!notificationsOpen)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors relative"
                  >
                    <Bell size={18} />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  <AnimatePresence>
                    {notificationsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-80 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                      >
                        {/* Header */}
                        <div className="p-4 border-b border-white/10">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-white">Notifications</h3>
                            <button
                              onClick={() => {
                                setNotifications(notifications.map(n => ({ ...n, read: true })));
                              }}
                              className="text-xs text-[#6366F1] hover:text-[#5558E3] transition-colors"
                            >
                              Mark all read
                            </button>
                          </div>
                        </div>

                        {/* Notifications List */}
                        <div className="max-h-96 overflow-y-auto">
                          {notifications.length > 0 ? (
                            notifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`p-4 border-b border-white/10 hover:bg-white/5 transition-colors cursor-pointer ${
                                  !notification.read ? 'bg-white/5' : ''
                                }`}
                                onClick={() => {
                                  setNotifications(
                                    notifications.map(n =>
                                      n.id === notification.id ? { ...n, read: true } : n
                                    )
                                  );
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  {/* Icon based on type */}
                                  <div className={`p-2 rounded-lg ${
                                    notification.type === 'user' ? 'bg-blue-500/20' :
                                    notification.type === 'payment' ? 'bg-green-500/20' :
                                    'bg-purple-500/20'
                                  }`}>
                                    {notification.type === 'user' && <UsersThree size={16} className="text-blue-400" />}
                                    {notification.type === 'payment' && <CurrencyDollar size={16} className="text-green-400" />}
                                    {notification.type === 'system' && <GearSix size={16} className="text-purple-400" />}
                                  </div>

                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-white mb-1">
                                      {notification.title}
                                    </p>
                                    <p className="text-xs text-gray-400 mb-1">
                                      {notification.message}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {notification.time}
                                    </p>
                                  </div>

                                  {!notification.read && (
                                    <div className="w-2 h-2 bg-[#6366F1] rounded-full mt-2"></div>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-8 text-center">
                              <Bell size={48} className="text-gray-500 mx-auto mb-3" />
                              <p className="text-gray-400 text-sm">No notifications</p>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="p-3 border-t border-white/10 bg-white/5">
                          <button
                            onClick={() => {
                              setNotificationsOpen(false);
                              navigate('/admin/notifications');
                            }}
                            className="w-full text-center text-sm text-[#6366F1] hover:text-[#5558E3] transition-colors"
                          >
                            View all notifications →
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
